import { useState, useCallback } from 'react'

export type ToggleModalFn<T> = (newState: T | null) => void

/**
 * a hook to help manage the state of a CIMSModal
 *
 * returns 3 stateful variables
 *
 * - a boolean to determine whether the modal is visible
 * - a function that toggles the modal and updates any internal data
 * - a generic type which represents the current state of the internal data
 *
 * if you _absolutely_ have to have the return value as an object use {@link useCIMSModalValues}
 *
 * @see {@link ToggleModalFn}
 */
export function useCIMSModal<T = unknown>(): [
  boolean,
  ToggleModalFn<T>,
  T | null
] {
  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<T | null>(null)
  /**
   * what happens if we always set internal state and set it null if `undefined`
   * is passed in, or possibly force T | null as the new state value
   *
   * @expires 2024-03-01
   */
  const toggleFn = useCallback<ToggleModalFn<T>>((state) => {
    setState(state)
    setVisible((prevState) => !prevState)
  }, [])
  return [visible, toggleFn, state]
}

/**
 * for most instances, you should use {@link useCIMSModal}
 *
 * if you _absolutely_ need the return value to be an object, you can use this
 *
 * @see {@link ToggleModalFn}
 * @see {@link UseCIMSModalValues}
 */
export function useCIMSModalValues<T = unknown>(): UseCIMSModalValues<T> {
  const [visible, toggle, state] = useCIMSModal<T>()
  return { visible, toggle, state }
}

/**
 * @see {@link useCIMSModal}
 */
export interface UseCIMSModalValues<T = unknown> {
  /**
   * whether the modal is visible
   */
  visible: boolean
  /**
   * toggles the modal and updates any internal state
   *
   * @param newState the new internal state
   * @see {@link ToggleModalFn}
   */
  toggle: ToggleModalFn<T>
  /**
   * the current internal state of the modal
   */
  state: T | null
}
